import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowRight, ArrowLeft, Building2, Users, Upload, FileText,
  CheckCircle2, Shield, Loader2, Eye, X, Crown, Zap
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { onboardPartner } from '../services/api';
import { toast } from 'sonner';
import Logo from '../components/Logo';

const TERMS_TEXT = `Mulberry World – Partner Terms & Conditions

Effective Date: 1st April 2026

Entity: Mulberry World ("the Platform", "we", "us", "our")

These Terms & Conditions ("Agreement") govern the onboarding, listing, and participation of partners ("Partner", "you", "your") on the Mulberry World platform. By submitting the onboarding form and selecting a subscription tier, you agree to be legally bound by this Agreement.

1. NATURE OF PLATFORM
Mulberry World operates as:
• A curated marketplace for luxury real estate and investment opportunities
• A lead generation and distribution platform
• A facilitator, not a principal to any transaction

The Platform does not:
• Own, sell, or transfer any property
• Provide legal, tax, or financial advice
• Guarantee transaction outcomes

2. PARTNER ELIGIBILITY & REPRESENTATION
By onboarding, you confirm:
• You are a legally registered entity or authorized representative
• You have the right to market and promote listed properties
• All information provided is accurate, lawful, and not misleading
• You comply with all applicable local laws and licensing requirements

3. LISTINGS & CONTENT
The Partner is solely responsible for all listing data, including images, pricing, descriptions, and legal compliance.

The Platform reserves the right to:
• Approve, reject, edit, or remove listings
• Reformat content for consistency
• Prioritize or deprioritize listings

You grant the Platform a non-exclusive, global, royalty-free license to use and promote your content across channels.

4. SUBSCRIPTION TIERS & PRICING
Partners may subscribe to one of the following plans:

4.1 Basic Plan (Free)
• Maximum 5 active listings
• No access to analytics or AI brochure extraction
• No default credits
• Mandatory wallet recharge required for lead access
• 10 credits consumed per lead

4.2 Professional Plan
Pricing:
• $399/month
• $3,990/year (approx. 16% discount)
• Maximum 25 active listings
• Access to lead analytics and AI brochure extraction
• Default allocation of 20 credits per listing (500 credits/month total)
• 10 credits consumed per lead (i.e. 2 leads per listing, or up to 50 leads/month)
• Credits are automatically renewed every 30 days for monthly plans, or annually for yearly plans

4.3 Enterprise Plan
Pricing:
• $999/month
• $9,990/year
• Unlimited listings
• Full access to analytics, AI brochure extraction, and lead system
• Unlimited lead access (no credit deduction)
• Priority visibility and platform features (as applicable)
• Credits are automatically renewed every 30 days for monthly plans, or annually for yearly plans

General Terms:
• All fees are non-refundable
• Fees must be paid in advance
• The Platform reserves the right to revise pricing with prior notice
• Non-payment may result in suspension or termination

5. LEAD MANAGEMENT & CREDIT SYSTEM
Leads generated through the Platform are distributed via a credit-based system. Partners must maintain sufficient credits to access leads.

Credit Usage:
• 10 credits per lead unlock

Additional Terms:
• Leads remain the property of the Platform until accessed
• Credits once used are non-refundable
• The Platform does not guarantee lead conversion or deal closure

6. COMMISSION & REVENUE SHARING
Commission and revenue sharing terms shall be governed by a separate Memorandum of Understanding (MoU) executed between the Partner and the Platform.

Partners agree to:
• Disclose all transactions originating from Platform-generated leads
• Honour all commercial terms agreed in the MoU

7. NON-CIRCUMVENTION
Partners shall not bypass the Platform to directly transact with leads introduced via the Platform. This obligation remains valid for 24 months from the date of lead introduction.

Violation may result in:
• Immediate termination
• Legal action
• Recovery of due commissions

8. BRAND USAGE & REPRESENTATION
Partners may not use Platform branding without written consent. The Platform may use Partner names, logos, and listings for promotional purposes.

9. DATA PRIVACY & CONFIDENTIALITY
Both parties agree to maintain confidentiality of all shared data. Partners must comply with applicable data protection laws. The Platform is not responsible for misuse of data by Partners.

10. LIMITATION OF LIABILITY
The Platform shall not be liable for:
• Inaccurate listings provided by Partners
• Transaction failures or disputes
• Financial, legal, or tax consequences
• Any indirect or consequential losses

Maximum liability (if applicable): Limited to fees paid in the last 3 months

11. INDEMNITY
The Partner agrees to indemnify and hold harmless the Platform against:
• Claims arising from misrepresentation
• Legal violations
• Disputes with buyers
• Incorrect or misleading listings

12. TERMINATION
The Platform may suspend or terminate access:
• For breach of terms
• For non-payment
• For reputational or legal risk

Partners may terminate with 30 days written notice. No refunds shall be issued upon termination.

13. MODIFICATIONS
The Platform reserves the right to update these Terms at any time. Continued usage constitutes acceptance of revised Terms.

14. GOVERNING LAW & JURISDICTION
This Agreement shall be governed by: Laws of India
Jurisdiction: Courts of Pune/Mumbai

15. ACCEPTANCE
By submitting the onboarding form and selecting a subscription plan, you confirm:
• You have read and understood these Terms
• You agree to be legally bound by them`;

const STEPS = [
  { id: 1, label: 'Plan & Contact', icon: Building2 },
  { id: 2, label: 'Operations', icon: Users },
  { id: 3, label: 'Documents & Terms', icon: FileText },
];

export default function PartnerOnboardingPage() {
  const [searchParams] = useSearchParams();
  const preselectedTier = searchParams.get('tier') || 'Basic';
  const preselectedCycle = searchParams.get('cycle') || 'monthly';

  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [mouFile, setMouFile] = useState(null);
  const [paymentFile, setPaymentFile] = useState(null);
  const [complianceDocs, setComplianceDocs] = useState({
    registration_cert: null, gst_cert: null, pan_card: null, rera_cert: null, company_profile: null
  });

  const [form, setForm] = useState({
    name: '', email: '', phone: '', company: '', designation: '',
    partner_type: 'developer', country: '', city: '', website: '',
    tier: preselectedTier, billing_cycle: preselectedCycle,
    years_in_business: '', portfolio_size: '', regions_of_operation: '',
    specialization: '', license_number: '', password: '', confirm_password: '',
    agreed_to_terms: false,
  });

  const updateField = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const canProceedStep1 = form.name && form.email && form.phone && form.company && form.password && form.password === form.confirm_password;
  const canProceedStep2 = true;
  const canSubmit = form.agreed_to_terms && canProceedStep1 && complianceDocs.registration_cert;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => {
        if (k !== 'confirm_password') fd.append(k, String(v));
      });
      if (mouFile) fd.append('mou_file', mouFile);
      if (paymentFile) fd.append('payment_proof', paymentFile);
      Object.entries(complianceDocs).forEach(([key, file]) => { if (file) fd.append(key, file); });
      await onboardPartner(fd);
      setSubmitted(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#0B0C10] flex items-center justify-center p-4">
        <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="bg-[#1A1D24] border border-[#2A2F3A] rounded-2xl p-10 max-w-md w-full text-center">
          <CheckCircle2 className="h-16 w-16 text-green-500 mx-auto mb-6" />
          <h2 className="text-2xl text-white font-semibold mb-3">Application Submitted</h2>
          <p className="text-[#A1A1AA] mb-2">Your <span className="text-[#D4AF37]">{form.tier}</span> plan onboarding application has been received.</p>
          <p className="text-[#A1A1AA] text-sm mb-8">Our team will review your documents and approve your account. You'll receive dashboard access upon approval.</p>
          <div className="flex gap-3 justify-center">
            <Link to="/"><Button variant="outline" className="border-[#2A2F3A] text-[#A1A1AA] hover:text-white">Back to Home</Button></Link>
            <Link to="/partner"><Button className="btn-luxury">Partner Login</Button></Link>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B0C10]">
      <header className="bg-[#1A1D24] border-b border-[#2A2F3A]">
        <div className="max-w-5xl mx-auto px-4 flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-3"><Logo size="small" showText={true} /></Link>
          <Link to="/partner/plans"><Button variant="ghost" className="text-[#A1A1AA] hover:text-white text-sm">View Plans</Button></Link>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 py-10">
        <div className="text-center mb-10">
          <Badge className="bg-[#D4AF37]/10 text-[#D4AF37] border-[#D4AF37]/30 mb-4">Partner Onboarding</Badge>
          <h1 className="text-3xl text-white font-semibold" data-testid="onboarding-heading">Start Your Partnership</h1>
          <p className="text-[#A1A1AA] mt-2">Complete the form below to get your partner dashboard access</p>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-center gap-2 mb-10">
          {STEPS.map((s, i) => (
            <div key={s.id} className="flex items-center">
              <button
                onClick={() => { if (s.id < step) setStep(s.id); }}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm transition-all ${step === s.id ? 'bg-[#D4AF37]/10 text-[#D4AF37] border border-[#D4AF37]/30' : step > s.id ? 'text-green-400' : 'text-[#A1A1AA]'}`}
                data-testid={`onboard-step-${s.id}`}
              >
                <s.icon className="h-4 w-4" />
                <span className="hidden sm:inline">{s.label}</span>
                <span className="sm:hidden">{s.id}</span>
              </button>
              {i < STEPS.length - 1 && <div className={`w-8 h-px mx-1 ${step > s.id ? 'bg-green-400' : 'bg-[#2A2F3A]'}`} />}
            </div>
          ))}
        </div>

        <div className="bg-[#1A1D24] border border-[#2A2F3A] rounded-2xl p-6 sm:p-8">
          {/* Step 1: Plan & Contact */}
          {step === 1 && (
            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="space-y-5">
              <h2 className="text-lg text-white font-medium mb-1">Select Plan & Contact Details</h2>

              {/* Plan selector */}
              <div>
                <Label className="text-white text-sm mb-2 block">Subscription Plan</Label>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { id: 'Basic', label: 'Basic', price: 'Free', icon: Building2, color: '#A1A1AA' },
                    { id: 'Professional', label: 'Professional', price: '$399/mo', icon: Zap, color: '#D4AF37' },
                    { id: 'Enterprise', label: 'Enterprise', price: '$999/mo', icon: Crown, color: '#8b5cf6' },
                  ].map(p => (
                    <button
                      key={p.id} type="button"
                      onClick={() => updateField('tier', p.id)}
                      className={`p-4 rounded-xl border text-center transition-all ${form.tier === p.id ? 'border-[#D4AF37] bg-[#D4AF37]/5' : 'border-[#2A2F3A] hover:border-[#3A3F4A]'}`}
                      data-testid={`onboard-plan-${p.id.toLowerCase()}`}
                    >
                      <p.icon className="h-5 w-5 mx-auto mb-1" style={{ color: p.color }} />
                      <div className="text-white text-sm font-medium">{p.label}</div>
                      <div className="text-[#A1A1AA] text-xs">{p.price}</div>
                    </button>
                  ))}
                </div>
              </div>

              {form.tier !== 'Basic' && (
                <div>
                  <Label className="text-white text-sm mb-2 block">Billing Cycle</Label>
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      { id: 'monthly', label: 'Monthly', price: form.tier === 'Professional' ? '$399/mo' : '$999/mo' },
                      { id: 'annual', label: 'Annual', price: form.tier === 'Professional' ? '$3,990/yr' : '$9,990/yr', badge: 'Save ~17%' },
                    ].map(c => (
                      <button
                        key={c.id} type="button"
                        onClick={() => updateField('billing_cycle', c.id)}
                        className={`p-3 rounded-xl border text-left transition-all ${form.billing_cycle === c.id ? 'border-[#D4AF37] bg-[#D4AF37]/5' : 'border-[#2A2F3A]'}`}
                        data-testid={`onboard-cycle-${c.id}`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-white text-sm">{c.label}</span>
                          {c.badge && <Badge className="bg-green-500/20 text-green-400 border-0 text-[10px]">{c.badge}</Badge>}
                        </div>
                        <div className="text-[#D4AF37] text-xs mt-0.5">{c.price}</div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-white text-sm mb-1.5 block">Full Name *</Label>
                  <Input value={form.name} onChange={e => updateField('name', e.target.value)} className="bg-[#0B0C10] border-[#2A2F3A] text-white" placeholder="John Smith" data-testid="onboard-name" />
                </div>
                <div>
                  <Label className="text-white text-sm mb-1.5 block">Email *</Label>
                  <Input type="email" value={form.email} onChange={e => updateField('email', e.target.value)} className="bg-[#0B0C10] border-[#2A2F3A] text-white" placeholder="partner@company.com" data-testid="onboard-email" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-white text-sm mb-1.5 block">Phone *</Label>
                  <Input value={form.phone} onChange={e => updateField('phone', e.target.value)} className="bg-[#0B0C10] border-[#2A2F3A] text-white" placeholder="+91 9999999999" data-testid="onboard-phone" />
                </div>
                <div>
                  <Label className="text-white text-sm mb-1.5 block">Company Name *</Label>
                  <Input value={form.company} onChange={e => updateField('company', e.target.value)} className="bg-[#0B0C10] border-[#2A2F3A] text-white" placeholder="Your Company" data-testid="onboard-company" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-white text-sm mb-1.5 block">Designation</Label>
                  <Input value={form.designation} onChange={e => updateField('designation', e.target.value)} className="bg-[#0B0C10] border-[#2A2F3A] text-white" placeholder="CEO, Director, etc." data-testid="onboard-designation" />
                </div>
                <div>
                  <Label className="text-white text-sm mb-1.5 block">Partner Type</Label>
                  <Select value={form.partner_type} onValueChange={v => updateField('partner_type', v)}>
                    <SelectTrigger className="bg-[#0B0C10] border-[#2A2F3A] text-white" data-testid="onboard-partner-type"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#1A1D24] border-[#2A2F3A]">
                      <SelectItem value="developer" className="text-white">Developer Partner</SelectItem>
                      <SelectItem value="agent" className="text-white">Agent Partner</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-white text-sm mb-1.5 block">Create Password *</Label>
                  <Input type="password" value={form.password} onChange={e => updateField('password', e.target.value)} className="bg-[#0B0C10] border-[#2A2F3A] text-white" placeholder="Min 8 characters" data-testid="onboard-password" />
                </div>
                <div>
                  <Label className="text-white text-sm mb-1.5 block">Confirm Password *</Label>
                  <Input type="password" value={form.confirm_password} onChange={e => updateField('confirm_password', e.target.value)} className="bg-[#0B0C10] border-[#2A2F3A] text-white" placeholder="Re-enter password" data-testid="onboard-confirm-password" />
                  {form.confirm_password && form.password !== form.confirm_password && (
                    <p className="text-red-400 text-xs mt-1">Passwords do not match</p>
                  )}
                </div>
              </div>

              <div className="flex justify-end pt-2">
                <Button type="button" className="btn-luxury h-10" onClick={() => setStep(2)} disabled={!canProceedStep1} data-testid="onboard-next-1">
                  Next: Operations <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </div>
            </motion.div>
          )}

          {/* Step 2: Operational Details */}
          {step === 2 && (
            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="space-y-5">
              <h2 className="text-lg text-white font-medium mb-1">Operational Details</h2>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-white text-sm mb-1.5 block">Country</Label>
                  <Input value={form.country} onChange={e => updateField('country', e.target.value)} className="bg-[#0B0C10] border-[#2A2F3A] text-white" placeholder="Primary country" data-testid="onboard-country" />
                </div>
                <div>
                  <Label className="text-white text-sm mb-1.5 block">City</Label>
                  <Input value={form.city} onChange={e => updateField('city', e.target.value)} className="bg-[#0B0C10] border-[#2A2F3A] text-white" placeholder="HQ city" data-testid="onboard-city" />
                </div>
              </div>
              <div>
                <Label className="text-white text-sm mb-1.5 block">Company Website</Label>
                <Input value={form.website} onChange={e => updateField('website', e.target.value)} className="bg-[#0B0C10] border-[#2A2F3A] text-white" placeholder="https://..." data-testid="onboard-website" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-white text-sm mb-1.5 block">Years in Business</Label>
                  <Select value={form.years_in_business} onValueChange={v => updateField('years_in_business', v)}>
                    <SelectTrigger className="bg-[#0B0C10] border-[#2A2F3A] text-white" data-testid="onboard-years"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent className="bg-[#1A1D24] border-[#2A2F3A]">
                      {['Less than 1 year', '1-3 years', '3-5 years', '5-10 years', '10+ years'].map(y => (
                        <SelectItem key={y} value={y} className="text-white">{y}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-white text-sm mb-1.5 block">Portfolio Size</Label>
                  <Select value={form.portfolio_size} onValueChange={v => updateField('portfolio_size', v)}>
                    <SelectTrigger className="bg-[#0B0C10] border-[#2A2F3A] text-white" data-testid="onboard-portfolio"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent className="bg-[#1A1D24] border-[#2A2F3A]">
                      {['1-5 properties', '5-20 properties', '20-50 properties', '50-100 properties', '100+ properties'].map(s => (
                        <SelectItem key={s} value={s} className="text-white">{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="text-white text-sm mb-1.5 block">Regions of Operation</Label>
                <Input value={form.regions_of_operation} onChange={e => updateField('regions_of_operation', e.target.value)} className="bg-[#0B0C10] border-[#2A2F3A] text-white" placeholder="e.g., UAE, Greece, Thailand" data-testid="onboard-regions" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-white text-sm mb-1.5 block">Specialization</Label>
                  <Input value={form.specialization} onChange={e => updateField('specialization', e.target.value)} className="bg-[#0B0C10] border-[#2A2F3A] text-white" placeholder="e.g., Luxury villas, Penthouses" data-testid="onboard-specialization" />
                </div>
                <div>
                  <Label className="text-white text-sm mb-1.5 block">License / Registration Number</Label>
                  <Input value={form.license_number} onChange={e => updateField('license_number', e.target.value)} className="bg-[#0B0C10] border-[#2A2F3A] text-white" placeholder="RERA/License no." data-testid="onboard-license" />
                </div>
              </div>

              <div className="flex justify-between pt-2">
                <Button type="button" variant="outline" className="border-[#2A2F3A] text-[#A1A1AA] hover:text-white h-10" onClick={() => setStep(1)} data-testid="onboard-back-2">
                  <ArrowLeft className="h-4 w-4 mr-2" /> Back
                </Button>
                <Button type="button" className="btn-luxury h-10" onClick={() => setStep(3)} data-testid="onboard-next-2">
                  Next: Documents <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </div>
            </motion.div>
          )}

          {/* Step 3: Documents & Terms */}
          {step === 3 && (
            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="space-y-5">
              <h2 className="text-lg text-white font-medium mb-1">Documents & Agreement</h2>

              <div className="bg-[#0B0C10] border border-[#2A2F3A] rounded-xl p-5 space-y-4">
                <h3 className="text-white text-sm font-medium">Upload Documents</h3>
                <p className="text-[#A1A1AA] text-xs">Upload your signed MoU and payment confirmation (if applicable for your plan).</p>

                <div>
                  <Label className="text-white text-sm mb-1.5 block">MoU / Agreement Document</Label>
                  <label className={`flex items-center gap-3 p-4 rounded-xl border-2 border-dashed cursor-pointer transition-all ${mouFile ? 'border-green-500/40 bg-green-500/5' : 'border-[#2A2F3A] hover:border-[#D4AF37]/40'}`}>
                    <input type="file" className="hidden" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" onChange={e => setMouFile(e.target.files[0])} data-testid="onboard-mou-input" />
                    {mouFile ? (
                      <><CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0" /><div><p className="text-white text-sm">{mouFile.name}</p><p className="text-green-400 text-xs">{(mouFile.size / 1024).toFixed(0)} KB</p></div></>
                    ) : (
                      <><Upload className="h-5 w-5 text-[#A1A1AA] flex-shrink-0" /><div><p className="text-[#A1A1AA] text-sm">Click to upload MoU</p><p className="text-[#A1A1AA] text-xs">PDF, DOC, DOCX, JPG, PNG</p></div></>
                    )}
                  </label>
                </div>

                {form.tier !== 'Basic' && (
                  <div>
                    <Label className="text-white text-sm mb-1.5 block">Payment Confirmation</Label>
                    <label className={`flex items-center gap-3 p-4 rounded-xl border-2 border-dashed cursor-pointer transition-all ${paymentFile ? 'border-green-500/40 bg-green-500/5' : 'border-[#2A2F3A] hover:border-[#D4AF37]/40'}`}>
                      <input type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" onChange={e => setPaymentFile(e.target.files[0])} data-testid="onboard-payment-input" />
                      {paymentFile ? (
                        <><CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0" /><div><p className="text-white text-sm">{paymentFile.name}</p><p className="text-green-400 text-xs">{(paymentFile.size / 1024).toFixed(0)} KB</p></div></>
                      ) : (
                        <><Upload className="h-5 w-5 text-[#A1A1AA] flex-shrink-0" /><div><p className="text-[#A1A1AA] text-sm">Click to upload payment proof</p><p className="text-[#A1A1AA] text-xs">Bank transfer receipt, invoice, etc.</p></div></>
                      )}
                    </label>
                  </div>
                )}
              </div>

              {/* Compliance Documents */}
              <div className="bg-[#0B0C10] border border-[#2A2F3A] rounded-xl p-5 space-y-4">
                <h3 className="text-white text-sm font-medium">Company Documents (for Indian Legal Compliance)</h3>
                <p className="text-[#A1A1AA] text-xs">Upload relevant company documents for verification. Accepted: PDF, DOC, JPG, PNG.</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {[
                    { key: 'registration_cert', label: 'Company Registration Certificate', required: true },
                    { key: 'gst_cert', label: 'GST Certificate', required: false },
                    { key: 'pan_card', label: 'PAN Card', required: false },
                    { key: 'rera_cert', label: 'RERA Certificate (if applicable)', required: false },
                    { key: 'company_profile', label: 'Company Profile / Brochure', required: false },
                  ].map(doc => (
                    <label key={doc.key} className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer transition-all text-sm ${complianceDocs[doc.key] ? 'border-green-500/40 bg-green-500/5' : doc.required && !complianceDocs[doc.key] ? 'border-red-500/30 hover:border-[#D4AF37]/30' : 'border-[#2A2F3A] hover:border-[#D4AF37]/30'}`}>
                      <input type="file" className="hidden" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" onChange={e => setComplianceDocs(prev => ({ ...prev, [doc.key]: e.target.files[0] }))} data-testid={`onboard-${doc.key}`} />
                      {complianceDocs[doc.key] ? (
                        <><CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0" /><span className="text-white truncate">{complianceDocs[doc.key].name}</span></>
                      ) : (
                        <><Upload className="h-4 w-4 text-[#A1A1AA] flex-shrink-0" /><span className="text-[#A1A1AA]">{doc.label}{doc.required ? <span className="text-red-400 ml-1">*</span> : <span className="text-[#555] ml-1">(Optional)</span>}</span></>
                      )}
                    </label>
                  ))}
                </div>
              </div>

              {/* T&C Checkbox */}
              <div className="bg-[#0B0C10] border border-[#2A2F3A] rounded-xl p-5">
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    checked={form.agreed_to_terms}
                    onChange={e => updateField('agreed_to_terms', e.target.checked)}
                    className="mt-1 h-4 w-4 rounded border-[#2A2F3A] accent-[#D4AF37]"
                    data-testid="onboard-terms-checkbox"
                  />
                  <div>
                    <p className="text-white text-sm">
                      I agree to the{' '}
                      <button
                        type="button"
                        onClick={() => setShowTerms(true)}
                        className="text-[#D4AF37] underline hover:text-[#D4AF37]/80"
                        data-testid="onboard-view-terms"
                      >
                        Mulberry World Partner Terms & Conditions
                      </button>
                    </p>
                    <p className="text-[#A1A1AA] text-xs mt-1">By checking this box, you confirm that you have read and agree to be legally bound by the Terms.</p>
                  </div>
                </div>
              </div>

              {/* Summary */}
              <div className="bg-[#D4AF37]/5 border border-[#D4AF37]/20 rounded-xl p-5">
                <h3 className="text-[#D4AF37] text-sm font-medium mb-3">Application Summary</h3>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="text-[#A1A1AA]">Plan:</div><div className="text-white">{form.tier} {form.tier !== 'Basic' ? `(${form.billing_cycle})` : ''}</div>
                  <div className="text-[#A1A1AA]">Company:</div><div className="text-white">{form.company || '-'}</div>
                  <div className="text-[#A1A1AA]">Contact:</div><div className="text-white">{form.name || '-'} ({form.email || '-'})</div>
                  <div className="text-[#A1A1AA]">Type:</div><div className="text-white capitalize">{form.partner_type} Partner</div>
                  <div className="text-[#A1A1AA]">Documents:</div><div className="text-white">{mouFile ? 'MoU uploaded' : 'No MoU'}{paymentFile ? ', Payment uploaded' : ''}</div>
                </div>
              </div>

              <div className="flex justify-between pt-2">
                <Button type="button" variant="outline" className="border-[#2A2F3A] text-[#A1A1AA] hover:text-white h-10" onClick={() => setStep(2)} data-testid="onboard-back-3">
                  <ArrowLeft className="h-4 w-4 mr-2" /> Back
                </Button>
                <Button
                  type="button"
                  className="btn-luxury h-10"
                  disabled={!canSubmit || submitting}
                  onClick={handleSubmit}
                  data-testid="onboard-submit"
                >
                  {submitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Submitting...</> : <>Submit Application <Shield className="h-4 w-4 ml-2" /></>}
                </Button>
              </div>
            </motion.div>
          )}
        </div>
      </div>

      {/* T&C Modal */}
      <AnimatePresence>
        {showTerms && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={e => { if (e.target === e.currentTarget) setShowTerms(false); }}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
              className="bg-[#1A1D24] border border-[#2A2F3A] rounded-2xl max-w-2xl w-full max-h-[85vh] flex flex-col"
              data-testid="terms-modal"
            >
              <div className="flex items-center justify-between p-5 border-b border-[#2A2F3A] flex-shrink-0">
                <div className="flex items-center gap-2">
                  <Shield className="h-5 w-5 text-[#D4AF37]" />
                  <h3 className="text-white font-semibold">Partner Terms & Conditions</h3>
                </div>
                <button onClick={() => setShowTerms(false)} className="text-[#A1A1AA] hover:text-white"><X className="h-5 w-5" /></button>
              </div>
              <div className="overflow-y-auto p-6 flex-1">
                <pre className="text-[#A1A1AA] text-sm whitespace-pre-wrap font-sans leading-relaxed">{TERMS_TEXT}</pre>
              </div>
              <div className="p-4 border-t border-[#2A2F3A] flex-shrink-0">
                <Button className="btn-luxury w-full h-10" onClick={() => { setShowTerms(false); updateField('agreed_to_terms', true); }} data-testid="terms-accept-btn">
                  I've Read & Accept the Terms
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
